/**
 * Deep Links Handler для Event Master Mobile App
 * Обрабатывает eventmaster:// и https://eventmaster.events
 *
 * Event Master v1.5 — Deep Links Integration
 *
 * Поддерживаемые сценарии:
 * 1. Cold start — приложение полностью убито, открыто через deep link
 * 2. Warm start — приложение в фоне, открыто через deep link
 *
 * Безопасность:
 * - Whitelist допустимых путей (защита от path injection)
 * - Валидация схемы URL
 * - Изолированный парсер (тестируемый отдельно)
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

/**
 * Whitelist допустимых префиксов путей для deep link навигации.
 *
 * SECURITY: без этого списка злоумышленник может передать любой путь
 * через deep link (path injection). Список должен совпадать с реальными
 * маршрутами в App.tsx.
 */
const ALLOWED_PATH_PREFIXES: string[] = [
  '/dashboard',
  '/events',
  '/marketplace',
  '/orders',
  '/services',
  '/settings',
  '/tasks',
  '/analytics',
  '/finance',
  '/documents',
  '/files',
  '/chats',
  '/notifications',
  '/team',
  '/partners',
  '/referral',
  '/subscription',
  '/pricing',
  '/support',
  '/video-calls',
  '/mobile',
  '/collaboration',
  '/personalization',
  '/monitoring',
  '/organizer',
  '/supplier',
  '/participant',
  '/speaker',
  '/manager',
  '/guest',
];

/**
 * Хосты, с которых принимаются Universal Links / App Links.
 * Совпадает с `android:host` в AndroidManifest.xml.
 */
const APP_LINK_HOSTS = new Set<string>([
  'eventmaster.events',
  'www.eventmaster.events',
]);

/**
 * Проверяет, находится ли путь в whitelist допустимых маршрутов.
 *
 * Exported для возможности unit-тестирования.
 */
export const isAllowedDeepLinkPath = (path: string): boolean => {
  if (path === '/') return true;
  return ALLOWED_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
};

/**
 * Парсит URL deep link и возвращает путь React Router.
 *
 * Поддерживаемые форматы:
 *   eventmaster://events/123         → /events/123
 *   eventmaster:///events/123        → /events/123  (тройной слеш — некоторые Android)
 *   eventmaster://                   → /
 *   https://eventmaster.events/events/123?tab=info → /events/123?tab=info
 *
 * Returns null если URL не распознан как deep link Event Master.
 *
 * Exported для возможности unit-тестирования.
 */
export const parseDeepLinkUrl = (url: string): string | null => {
  try {
    // Custom scheme: eventmaster:// или eventmaster:///
    if (url.startsWith('eventmaster://')) {
      // Убираем схему, обрабатываем как двойной (//) так и тройной (///) слеш
      const withoutScheme = url.replace(/^eventmaster:\/\/\/?/, '');
      // Пустой путь → корень
      return withoutScheme ? `/${withoutScheme}` : '/';
    }

    // Universal Links: https://eventmaster.events/...
    // Сверяем именно hostname, а не подстроку: домен короткий и встречается
    // в query чужих ссылок — `https://evil.example/?x=eventmaster.events`
    // не должен задавать внутренний маршрут приложения.
    const parsed = new URL(url);
    if (APP_LINK_HOSTS.has(parsed.hostname.toLowerCase())) {
      // Сохраняем pathname + query string + hash (не обрезаем)
      const path = parsed.pathname || '/';
      const search = parsed.search || '';
      const hash = parsed.hash || '';
      return `${path}${search}${hash}`;
    }

    // URL не относится к Event Master
    return null;
  } catch {
    // Невалидный URL — не падаем, возвращаем null
    return null;
  }
};

/**
 * Хук для обработки Deep Links в React Router.
 *
 * Автоматически навигирует на правильный роут при открытии приложения
 * через eventmaster:// схему или Universal Link (https://eventmaster.events).
 *
 * Обрабатывает оба сценария:
 * - Cold start: приложение убито → getLaunchUrl()
 * - Warm start: приложение в фоне → appUrlOpen event
 *
 * Интеграция с ProtectedRoute:
 * Если целевой маршрут требует аутентификации, ProtectedRoute автоматически
 * сохраняет location.state.from и после логина перенаправляет на него.
 *
 * @example
 * const DeepLinkHandler = () => { useDeepLinks(); return null; };
 * // Размещать внутри <Router> в App.tsx
 */
export const useDeepLinks = () => {
  const navigate = useNavigate();
  // Предотвращает двойную обработку getLaunchUrl при re-рендере
  const hasHandledColdStart = useRef(false);

  useEffect(() => {
    // Хук работает только в нативном приложении (iOS/Android)
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    /**
     * Основная функция обработки deep link URL.
     * Вызывается как при cold start, так и при warm start.
     */
    const handleDeepLink = (url: string, _source: 'cold-start' | 'warm-start'): void => {
      const path = parseDeepLinkUrl(url);

      if (!path) {
        console.warn('⚠️ Deep Link: URL не распознан как Event Master deep link:', url);
        return;
      }

      // SECURITY: проверяем путь по whitelist перед навигацией
      if (!isAllowedDeepLinkPath(path)) {
        console.warn('⚠️ Deep Link: Путь не в whitelist, навигация заблокирована:', path);
        return;
      }
      // replace: false — сохраняем deep link в истории браузера,
      // чтобы пользователь мог нажать "назад"
      navigate(path, { replace: false });
    };

    // ─────────────────────────────────────────────────────────
    // 1. COLD START: приложение полностью убито, открыто через deep link
    //
    // appUrlOpen НЕ срабатывает при cold start в Capacitor,
    // поэтому обязательно вызываем getLaunchUrl() при монтировании.
    // ─────────────────────────────────────────────────────────
    if (!hasHandledColdStart.current) {
      hasHandledColdStart.current = true;

      CapacitorApp.getLaunchUrl()
        .then((result) => {
          if (result?.url) {
            handleDeepLink(result.url, 'cold-start');
          }
        })
        .catch((error) => {
          // getLaunchUrl() может бросить ошибку на некоторых устройствах —
          // не блокируем загрузку приложения
          console.error('❌ Deep Link: getLaunchUrl() error:', error);
        });
    }

    // ─────────────────────────────────────────────────────────
    // 2. WARM START: приложение уже запущено/в фоне
    //
    // appUrlOpen срабатывает при открытии deep link пока app работает.
    // ─────────────────────────────────────────────────────────
    const handlerPromise = CapacitorApp.addListener('appUrlOpen', (event) => {
      handleDeepLink(event.url, 'warm-start');
    });

    // Cleanup: удаляем listener при unmount компонента
    return () => {
      handlerPromise.then((handler) => handler.remove());
    };
  }, [navigate]);
};
